/**
 * AuditLogPage - Administratorenansicht für den Audit-Trail
 * 
 * Features:
 * - Tabelle mit allen Audit-Einträgen
 * - Filter nach Zeitraum, Bearbeiter, Mitarbeiter, Datenart, Aktionstyp
 * - Serverseitige Paginierung
 * - Detail-Modal mit Vorher/Nachher
 * - CSV-Export
 * - Hash-Chain-Verifizierung
 * - Tab für gelöschte Einträge mit Wiederherstellung
 */
import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { motion } from 'framer-motion';
import { format, startOfMonth, endOfMonth, subMonths } from 'date-fns';
import { de } from 'date-fns/locale';
import { toast } from 'sonner';
import { safeFormatDate, formatDateTimeBerlin } from '../lib/dateTime';
import {
  History,
  Shield,
  ShieldCheck,
  ShieldAlert,
  Download,
  RefreshCw,
  Search,
  Filter,
  X,
  ChevronLeft,
  ChevronRight,
  Eye,
  RotateCcw,
  Trash2,
  AlertTriangle,
  User,
  Calendar,
  Database,
  FileText,
  CheckCircle,
  XCircle,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { ChangeReasonDialog } from '../components/ChangeReasonDialog';
import { 
  FIELD_TRANSLATIONS, 
  ACTION_LABELS, 
  COLLECTION_LABELS,
  translateFieldName,
  formatValue,
} from '../components/AuditHistoryPanel';

// Status-Labels für Rollen
const ROLE_LABELS = {
  admin: 'Administrator',
  supervisor: 'Vorgesetzter',
  accounting: 'Buchhaltung',
  employee: 'Mitarbeiter',
  system: 'System',
};

export default function AuditLogPage() {
  const { user, api } = useAuth();
  const [activeTab, setActiveTab] = useState('log');
  
  // Audit Log State
  const [auditEntries, setAuditEntries] = useState([]);
  const [totalEntries, setTotalEntries] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(25);
  const [isLoading, setIsLoading] = useState(true);
  
  // Filters
  const [filters, setFilters] = useState({
    startDate: format(subMonths(new Date(), 1), 'yyyy-MM-dd'),
    endDate: format(new Date(), 'yyyy-MM-dd'),
    userId: '',
    ownerUserId: '',
    collectionName: '',
    action: '',
    foreignOnly: false,
  });
  const [showFilters, setShowFilters] = useState(false);
  
  // Users List
  const [users, setUsers] = useState([]);
  
  // Verification State
  const [verificationResult, setVerificationResult] = useState(null);
  const [isVerifying, setIsVerifying] = useState(false);
  
  // Detail Modal
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  
  // Deleted Items State
  const [deletedItems, setDeletedItems] = useState([]);
  const [deletedLoading, setDeletedLoading] = useState(false);
  
  // Restore Dialog
  const [restoreDialogOpen, setRestoreDialogOpen] = useState(false);
  const [itemToRestore, setItemToRestore] = useState(null);
  const [isRestoring, setIsRestoring] = useState(false);

  // Fetch Users
  const fetchUsers = useCallback(async () => {
    try {
      const response = await api.get('/users');
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  }, [api]);

  // Fetch Audit Log
  const fetchAuditLog = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('skip', ((currentPage - 1) * pageSize).toString());
      params.append('limit', pageSize.toString());
      
      if (filters.startDate) params.append('start_date', filters.startDate);
      if (filters.endDate) params.append('end_date', filters.endDate);
      if (filters.userId) params.append('user_id', filters.userId);
      if (filters.ownerUserId) params.append('owner_user_id', filters.ownerUserId);
      if (filters.collectionName) params.append('collection_name', filters.collectionName);
      if (filters.action) params.append('action', filters.action);
      if (filters.foreignOnly) params.append('foreign_only', 'true');
      
      const response = await api.get(`/audit/log?${params.toString()}`);
      setAuditEntries(response.data.items || []);
      setTotalEntries(response.data.total || 0);
    } catch (error) {
      console.error('Failed to fetch audit log:', error);
      toast.error('Audit-Log konnte nicht geladen werden');
    } finally {
      setIsLoading(false);
    }
  }, [api, currentPage, pageSize, filters]);

  // Fetch Deleted Items
  const fetchDeletedItems = useCallback(async () => {
    setDeletedLoading(true);
    try {
      const response = await api.get('/audit/deleted');
      setDeletedItems(response.data || []);
    } catch (error) {
      console.error('Failed to fetch deleted items:', error);
      toast.error('Gelöschte Einträge konnten nicht geladen werden');
    } finally {
      setDeletedLoading(false);
    }
  }, [api]);

  // Verify Chain
  const handleVerifyChain = async () => {
    setIsVerifying(true);
    try {
      const response = await api.get('/audit/verify');
      setVerificationResult(response.data);
      if (response.data.valid) {
        toast.success(`Hash-Kette intakt: ${response.data.checked_count} Einträge geprüft`);
      } else {
        toast.error('Hash-Kette fehlerhaft! Siehe Details.');
      }
    } catch (error) {
      console.error('Verification failed:', error);
      toast.error('Verifizierung fehlgeschlagen');
    } finally {
      setIsVerifying(false);
    }
  };

  // Export CSV
  const handleExportCSV = async () => {
    try {
      const params = new URLSearchParams();
      params.append('format', 'csv');
      if (filters.startDate) params.append('start_date', filters.startDate);
      if (filters.endDate) params.append('end_date', filters.endDate);
      if (filters.userId) params.append('user_id', filters.userId);
      if (filters.ownerUserId) params.append('owner_user_id', filters.ownerUserId);
      if (filters.collectionName) params.append('collection_name', filters.collectionName);
      if (filters.action) params.append('action', filters.action);
      if (filters.foreignOnly) params.append('foreign_only', 'true');
      
      const response = await api.get(`/audit/export?${params.toString()}`, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `audit-log-${format(new Date(), 'yyyy-MM-dd')}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Export erfolgreich');
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Export fehlgeschlagen');
    }
  };

  // Restore Item
  const handleRestore = async (reason) => {
    if (!itemToRestore) return;
    
    setIsRestoring(true);
    try {
      await api.post(`/audit/restore/${itemToRestore.collection}/${itemToRestore.id}`, {
        reason
      });
      
      toast.success('Eintrag wurde wiederhergestellt');
      setRestoreDialogOpen(false);
      setItemToRestore(null);
      fetchDeletedItems();
      fetchAuditLog();
    } catch (error) {
      console.error('Restore failed:', error);
      toast.error(error.response?.data?.detail || 'Wiederherstellung fehlgeschlagen');
    } finally {
      setIsRestoring(false);
    }
  };

  // Initial Load
  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  useEffect(() => {
    if (activeTab === 'log') {
      fetchAuditLog();
    } else if (activeTab === 'deleted') {
      fetchDeletedItems();
    }
  }, [activeTab, fetchAuditLog, fetchDeletedItems]);

  // Reset page on filter change
  useEffect(() => {
    setCurrentPage(1);
  }, [filters]);

  const totalPages = Math.ceil(totalEntries / pageSize);

  // Get Action Badge
  const getActionBadge = (action) => {
    const config = ACTION_LABELS[action] || { label: action, color: 'bg-muted text-muted-foreground' };
    return (
      <Badge className={config.color}>
        {config.label}
      </Badge>
    );
  };

  // Format Changes Summary
  const formatChangesSummary = (changes) => {
    if (!changes || Object.keys(changes).length === 0) return '—';
    const keys = Object.keys(changes).slice(0, 3);
    const translated = keys.map(k => translateFieldName(k));
    const more = Object.keys(changes).length > 3 ? ` +${Object.keys(changes).length - 3}` : '';
    return translated.join(', ') + more;
  };

  return (
    <div data-testid="audit-log-page" className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground flex items-center gap-3">
            <History className="w-8 h-8 text-indigo-600 dark:text-indigo-400" />
            Änderungsprotokoll
          </h1>
          <p className="text-muted-foreground mt-1">GoBD-konformer Audit-Trail aller Datenänderungen</p>
        </div>
      </div>

      {/* Verification Status */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="card-glass p-4"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
              verificationResult?.valid === true ? 'bg-emerald-500/20' :
              verificationResult?.valid === false ? 'bg-red-500/20' :
              'bg-muted'
            }`}>
              {verificationResult?.valid === true ? (
                <ShieldCheck className="w-6 h-6 text-emerald-600 dark:text-emerald-400" />
              ) : verificationResult?.valid === false ? (
                <ShieldAlert className="w-6 h-6 text-red-600 dark:text-red-400" />
              ) : (
                <Shield className="w-6 h-6 text-muted-foreground" />
              )}
            </div>
            <div>
              <h3 className="text-foreground font-medium">Integrität der Aufzeichnungen</h3>
              {verificationResult ? (
                <p className={`text-sm ${verificationResult.valid ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`}>
                  {verificationResult.valid 
                    ? `Kette intakt, ${verificationResult.checked_count} Einträge geprüft`
                    : `${verificationResult.errors?.length || 0} Fehler gefunden`
                  }
                </p>
              ) : (
                <p className="text-sm text-muted-foreground">Noch nicht geprüft</p>
              )}
            </div>
          </div>
          
          <Button
            onClick={handleVerifyChain}
            disabled={isVerifying}
            className="bg-indigo-500/20 text-indigo-400 hover:bg-indigo-500/30 border border-indigo-500/30"
            data-testid="verify-chain-btn"
          >
            {isVerifying ? (
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Shield className="w-4 h-4 mr-2" />
            )}
            Jetzt prüfen
          </Button>
        </div>
        
        {verificationResult?.valid === false && verificationResult.errors?.length > 0 && (
          <div className="mt-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
            <p className="text-sm text-red-600 dark:text-red-400 font-medium mb-2">Gefundene Fehler:</p>
            <ul className="text-sm text-red-700 dark:text-red-300 space-y-1">
              {verificationResult.errors.slice(0, 5).map((error, i) => (
                <li key={i}>• {error}</li>
              ))}
              {verificationResult.errors.length > 5 && (
                <li className="text-muted-foreground">... und {verificationResult.errors.length - 5} weitere</li>
              )}
            </ul>
          </div>
        )}
      </motion.div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-card/50 border border-border">
          <TabsTrigger value="log" className="data-[state=active]:bg-indigo-500/20 data-[state=active]:text-indigo-400">
            <FileText className="w-4 h-4 mr-2" />
            Änderungsprotokoll
          </TabsTrigger>
          <TabsTrigger value="deleted" className="data-[state=active]:bg-red-500/20 data-[state=active]:text-red-400">
            <Trash2 className="w-4 h-4 mr-2" />
            Gelöschte Einträge
          </TabsTrigger>
        </TabsList>

        {/* Audit Log Tab */}
        <TabsContent value="log" className="mt-4">
          {/* Filters */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="card-glass p-4 mb-4"
          >
            <div className="flex items-center justify-between mb-4">
              <Button
                variant="ghost"
                onClick={() => setShowFilters(!showFilters)}
                className="text-muted-foreground hover:text-foreground"
              >
                <Filter className="w-4 h-4 mr-2" />
                Filter {showFilters ? 'ausblenden' : 'anzeigen'}
              </Button>
              
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  onClick={handleExportCSV}
                  className="text-muted-foreground hover:text-foreground"
                  data-testid="export-csv-btn"
                >
                  <Download className="w-4 h-4 mr-2" />
                  CSV Export
                </Button>
                <Button
                  variant="ghost"
                  onClick={fetchAuditLog}
                  className="text-muted-foreground hover:text-foreground"
                >
                  <RefreshCw className="w-4 h-4" />
                </Button>
              </div>
            </div>
            
            {showFilters && (
              <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">Von</label>
                  <Input
                    type="date"
                    value={filters.startDate}
                    onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
                    className="bg-input border-border text-foreground"
                  />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">Bis</label>
                  <Input
                    type="date"
                    value={filters.endDate}
                    onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
                    className="bg-input border-border text-foreground"
                  />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">Bearbeiter</label>
                  <Select value={filters.userId || "all"} onValueChange={(v) => setFilters({ ...filters, userId: v === "all" ? "" : v })}>
                    <SelectTrigger className="bg-input border-border text-foreground">
                      <SelectValue placeholder="Alle" />
                    </SelectTrigger>
                    <SelectContent className="bg-card border-border">
                      <SelectItem value="all">Alle</SelectItem>
                      {users.map((u) => (
                        <SelectItem key={u.id} value={u.id}>
                          {u.first_name} {u.last_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">Betroffener Mitarbeiter</label>
                  <Select value={filters.ownerUserId || "all"} onValueChange={(v) => setFilters({ ...filters, ownerUserId: v === "all" ? "" : v })}>
                    <SelectTrigger className="bg-input border-border text-foreground">
                      <SelectValue placeholder="Alle" />
                    </SelectTrigger>
                    <SelectContent className="bg-card border-border">
                      <SelectItem value="all">Alle</SelectItem>
                      {users.map((u) => (
                        <SelectItem key={u.id} value={u.id}>
                          {u.first_name} {u.last_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">Datenart</label>
                  <Select value={filters.collectionName || "all"} onValueChange={(v) => setFilters({ ...filters, collectionName: v === "all" ? "" : v })}>
                    <SelectTrigger className="bg-input border-border text-foreground">
                      <SelectValue placeholder="Alle" />
                    </SelectTrigger>
                    <SelectContent className="bg-card border-border">
                      <SelectItem value="all">Alle</SelectItem>
                      {Object.entries(COLLECTION_LABELS).map(([key, label]) => (
                        <SelectItem key={key} value={key}>{label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">Aktionstyp</label>
                  <Select value={filters.action || "all"} onValueChange={(v) => setFilters({ ...filters, action: v === "all" ? "" : v })}>
                    <SelectTrigger className="bg-input border-border text-foreground">
                      <SelectValue placeholder="Alle" />
                    </SelectTrigger>
                    <SelectContent className="bg-card border-border">
                      <SelectItem value="all">Alle</SelectItem>
                      {Object.entries(ACTION_LABELS).map(([key, config]) => (
                        <SelectItem key={key} value={key}>{config.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
            
            {showFilters && (
              <div className="mt-4 flex items-center gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={filters.foreignOnly}
                    onChange={(e) => setFilters({ ...filters, foreignOnly: e.target.checked })}
                    className="w-4 h-4 rounded border-white/20 bg-card text-indigo-600 dark:text-indigo-400"
                  />
                  <span className="text-sm text-muted-foreground">Nur Fremdänderungen</span>
                </label>
                
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setFilters({
                    startDate: format(subMonths(new Date(), 1), 'yyyy-MM-dd'),
                    endDate: format(new Date(), 'yyyy-MM-dd'),
                    userId: '',
                    ownerUserId: '',
                    collectionName: '',
                    action: '',
                    foreignOnly: false,
                  })}
                  className="text-muted-foreground hover:text-foreground"
                >
                  <X className="w-3 h-3 mr-1" />
                  Filter zurücksetzen
                </Button>
              </div>
            )}
          </motion.div>

          {/* Table */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="card-glass overflow-hidden"
          >
            {isLoading ? (
              <div className="flex items-center justify-center h-64">
                <div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : (
              <>
                <Table>
                  <TableHeader>
                    <TableRow className="border-border hover:bg-transparent">
                      <TableHead className="text-muted-foreground">Zeitpunkt</TableHead>
                      <TableHead className="text-muted-foreground">Bearbeiter</TableHead>
                      <TableHead className="text-muted-foreground">Aktion</TableHead>
                      <TableHead className="text-muted-foreground">Datenart</TableHead>
                      <TableHead className="text-muted-foreground">Betroffener</TableHead>
                      <TableHead className="text-muted-foreground">Änderungen</TableHead>
                      <TableHead className="text-muted-foreground">Grund</TableHead>
                      <TableHead className="text-muted-foreground w-12"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {auditEntries.map((entry) => (
                      <TableRow 
                        key={entry.id} 
                        className={`border-border hover:bg-accent cursor-pointer ${
                          entry.is_foreign_edit ? 'border-l-2 border-l-amber-500' : ''
                        }`}
                        onClick={() => { setSelectedEntry(entry); setDetailModalOpen(true); }}
                      >
                        <TableCell className="text-foreground text-sm">
                          {formatDateTimeBerlin(entry.timestamp, "d. MMM, HH:mm")}
                        </TableCell>
                        <TableCell className="text-muted-foreground text-sm">
                          {entry.user_name || 'System'}
                        </TableCell>
                        <TableCell>{getActionBadge(entry.action)}</TableCell>
                        <TableCell className="text-muted-foreground text-sm">
                          {COLLECTION_LABELS[entry.collection_name] || entry.collection_name}
                        </TableCell>
                        <TableCell className="text-muted-foreground text-sm">
                          {entry.owner_user_name || '—'}
                        </TableCell>
                        <TableCell className="text-muted-foreground text-sm max-w-[150px] truncate">
                          {formatChangesSummary(entry.changes)}
                        </TableCell>
                        <TableCell className="text-muted-foreground text-sm max-w-[100px] truncate">
                          {entry.reason || '—'}
                        </TableCell>
                        <TableCell>
                          <Button variant="ghost" size="sm" className="p-1">
                            <Eye className="w-4 h-4 text-muted-foreground" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>

                {/* Pagination */}
                <div className="flex items-center justify-between p-4 border-t border-border">
                  <p className="text-sm text-muted-foreground">
                    {totalEntries} Einträge gesamt
                  </p>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      className="text-muted-foreground"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <span className="text-sm text-muted-foreground">
                      Seite {currentPage} von {totalPages || 1}
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                      disabled={currentPage >= totalPages}
                      className="text-muted-foreground"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </>
            )}
          </motion.div>
        </TabsContent>

        {/* Deleted Items Tab */}
        <TabsContent value="deleted" className="mt-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="card-glass overflow-hidden"
          >
            {deletedLoading ? (
              <div className="flex items-center justify-center h-64">
                <div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : deletedItems.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
                <Trash2 className="w-12 h-12 mb-4 opacity-50" />
                <p>Keine gelöschten Einträge vorhanden</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="border-border hover:bg-transparent">
                    <TableHead className="text-muted-foreground">Datenart</TableHead>
                    <TableHead className="text-muted-foreground">Datum</TableHead>
                    <TableHead className="text-muted-foreground">Mitarbeiter</TableHead>
                    <TableHead className="text-muted-foreground">Gelöscht von</TableHead>
                    <TableHead className="text-muted-foreground">Gelöscht am</TableHead>
                    <TableHead className="text-muted-foreground">Löschgrund</TableHead>
                    <TableHead className="text-muted-foreground w-24">Aktion</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {deletedItems.map((item) => (
                    <TableRow key={`${item.collection}-${item.id}`} className="border-border hover:bg-accent">
                      <TableCell className="text-foreground text-sm">
                        {COLLECTION_LABELS[item.collection] || item.collection}
                      </TableCell>
                      <TableCell className="text-muted-foreground text-sm">
                        {safeFormatDate(item.date || item.created_at, "d. MMM yyyy", '—')}
                      </TableCell>
                      <TableCell className="text-muted-foreground text-sm">
                        {item.user_name || '—'}
                      </TableCell>
                      <TableCell className="text-muted-foreground text-sm">
                        {item.deleted_by_name || '—'}
                      </TableCell>
                      <TableCell className="text-muted-foreground text-sm">
                        {formatDateTimeBerlin(item.deleted_at, "d. MMM yyyy, HH:mm", '—')}
                      </TableCell>
                      <TableCell className="text-muted-foreground text-sm max-w-[200px] truncate">
                        {item.delete_reason || '—'}
                      </TableCell>
                      <TableCell>
                        {user?.role === 'admin' && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => { setItemToRestore(item); setRestoreDialogOpen(true); }}
                            className="text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/10"
                            data-testid={`restore-btn-${item.id}`}
                          >
                            <RotateCcw className="w-4 h-4 mr-1" />
                            Wiederherstellen
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </motion.div>
        </TabsContent>
      </Tabs>

      {/* Detail Modal */}
      <Dialog open={detailModalOpen} onOpenChange={setDetailModalOpen}>
        <DialogContent className="bg-card border-border max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-foreground flex items-center gap-2">
              <History className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
              Änderungsdetails
            </DialogTitle>
          </DialogHeader>
          
          {selectedEntry && (
            <div className="space-y-6">
              {/* Meta Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Zeitpunkt</p>
                  <p className="text-foreground">
                    {formatDateTimeBerlin(selectedEntry.timestamp, "d. MMMM yyyy, HH:mm:ss 'Uhr'")}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Bearbeiter</p>
                  <p className="text-foreground">
                    {selectedEntry.user_name || 'System'} 
                    <span className="text-muted-foreground text-sm ml-2">
                      ({ROLE_LABELS[selectedEntry.user_role] || selectedEntry.user_role})
                    </span>
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Aktion</p>
                  {getActionBadge(selectedEntry.action)}
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Datenart</p>
                  <p className="text-foreground">
                    {COLLECTION_LABELS[selectedEntry.collection_name] || selectedEntry.collection_name}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Sequenz</p>
                  <p className="text-foreground font-mono">{selectedEntry.sequence}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Dokument-ID</p>
                  <p className="text-foreground font-mono text-sm truncate">{selectedEntry.document_id}</p>
                </div>
              </div>

              {/* Foreign Edit Warning */}
              {selectedEntry.is_foreign_edit && (
                <div className="p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                  <p className="text-sm text-amber-700 dark:text-amber-300">
                    Fremdänderung: Dieser Eintrag wurde von einer anderen Person als dem Eigentümer geändert.
                  </p>
                </div>
              )}

              {/* Reason */}
              {selectedEntry.reason && (
                <div>
                  <p className="text-xs text-muted-foreground mb-2">Änderungsgrund</p>
                  <div className="p-3 bg-indigo-500/10 border border-indigo-500/20 rounded-lg">
                    <p className="text-indigo-700 dark:text-indigo-300">{selectedEntry.reason}</p>
                  </div>
                </div>
              )}

              {/* Changes */}
              {selectedEntry.changes && Object.keys(selectedEntry.changes).length > 0 && (
                <div>
                  <p className="text-xs text-muted-foreground mb-2">Geänderte Felder</p>
                  <div className="space-y-2">
                    {Object.entries(selectedEntry.changes).map(([field, change]) => (
                      <div key={field} className="flex items-start gap-4 p-3 bg-muted/50 rounded-lg">
                        <span className="text-muted-foreground min-w-[150px] font-medium">
                          {translateFieldName(field)}
                        </span>
                        <div className="flex items-center gap-2 flex-1">
                          <span className="text-red-600 dark:text-red-400/70 line-through">
                            {formatValue(change.old, field)}
                          </span>
                          <span className="text-muted-foreground">→</span>
                          <span className="text-emerald-600 dark:text-emerald-400">
                            {formatValue(change.new, field)}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Snapshots */}
              <div className="grid grid-cols-2 gap-4">
                {selectedEntry.snapshot_before && (
                  <div>
                    <p className="text-xs text-muted-foreground mb-2">Zustand vorher</p>
                    <pre className="p-3 bg-red-500/5 border border-red-500/10 rounded-lg text-xs text-muted-foreground overflow-auto max-h-48">
                      {JSON.stringify(selectedEntry.snapshot_before, null, 2)}
                    </pre>
                  </div>
                )}
                {selectedEntry.snapshot_after && (
                  <div>
                    <p className="text-xs text-muted-foreground mb-2">Zustand nachher</p>
                    <pre className="p-3 bg-emerald-500/5 border border-emerald-500/10 rounded-lg text-xs text-muted-foreground overflow-auto max-h-48">
                      {JSON.stringify(selectedEntry.snapshot_after, null, 2)}
                    </pre>
                  </div>
                )}
              </div>

              {/* Hash Info */}
              <div className="pt-4 border-t border-border">
                <p className="text-xs text-muted-foreground mb-2">Hash-Chain (technisch)</p>
                <div className="grid grid-cols-2 gap-4 text-xs">
                  <div>
                    <p className="text-muted-foreground">Prev Hash:</p>
                    <p className="font-mono text-muted-foreground truncate">{selectedEntry.prev_hash}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Hash:</p>
                    <p className="font-mono text-muted-foreground truncate">{selectedEntry.hash}</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Restore Dialog */}
      <ChangeReasonDialog
        open={restoreDialogOpen}
        onOpenChange={setRestoreDialogOpen}
        title="Eintrag wiederherstellen"
        description="Bitte geben Sie einen Grund für die Wiederherstellung an."
        onConfirm={handleRestore}
        onCancel={() => { setRestoreDialogOpen(false); setItemToRestore(null); }}
        isLoading={isRestoring}
        confirmButtonText="Wiederherstellen"
      />
    </div>
  );
}
